import { useState, useEffect, useRef } from 'react';
import { FaSave, FaBan, FaCheck, FaEllipsisV, FaFileContract, FaClipboardCheck, FaFileInvoiceDollar } from 'react-icons/fa';
import ConfirmationModal from '../../../../templates/ConfirmationModal';
import { useContractActions } from '../../hooks/contract/useContractActions';
import { AssignorBillingAddressData } from '../form/AssignorBillingAddress';

const STATUS_FINISHED = 'Finalizado com sucesso';

// Ação "Faturar" temporariamente desabilitada até a liberação para produção.
// Alterar para `true` para reexibir o botão de faturamento nas views de operação.
const BILLING_ACTION_ENABLED = false;

interface Props {
    onSave: () => void;
    onCancel: () => void;
    onFinish: () => void;
    onBilling?: () => void;
    onAmendment?: () => void;
    isLoading?: boolean;
    isFormDisabled?: boolean;
    hasContractId?: boolean;
    contractStatus?: string;
    canAmend?: boolean;
    isBilled?: boolean;
    // Necessários para a ação "Solicitar Registro" (encapsulada aqui).
    creditOperationId?: string;
    apiUrl?: string;
    apiKey?: string;
    setLoading?: (value: boolean) => void;
    onRegistered?: () => void;
    billingAddress?: AssignorBillingAddressData | null;
    requiresBillingAddress?: boolean;
}

const ContractActionDropdown: React.FC<Props> = ({
    onSave,
    onCancel,
    onFinish,
    onBilling,
    onAmendment,
    isLoading = false,
    isFormDisabled = false,
    hasContractId = false,
    contractStatus = '',
    canAmend = true,
    isBilled = false,
    creditOperationId,
    apiUrl = '',
    apiKey = '',
    setLoading,
    onRegistered,
    billingAddress,
    requiresBillingAddress = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const { handleRequestRegister } = useContractActions({
        apiUrl,
        apiKey,
        creditOperationId,
        setLoading: setLoading || (() => { }),
        setIsCancelModalOpen: () => { },
        setIsFinishModalOpen: () => { },
        onRefresh: onRegistered,
        contractStatus,
        billingAddress,
        requiresBillingAddress,
    });

    const canRequestRegister = !!creditOperationId && contractStatus === STATUS_FINISHED;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const itemClass = 'group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 w-full text-left disabled:opacity-50 disabled:cursor-not-allowed';

    return (
        <div ref={ref} className="relative inline-block text-left ml-2">
            <button type="button"
                className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={() => setIsOpen(!isOpen)}
                disabled={isLoading}
            >
                Ações
                <FaEllipsisV className="ml-2 h-4 w-4" />
            </button>

            {isOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                    <div className="py-1">
                        <button type="button"
                            className={itemClass}
                            onClick={() => { onSave(); setIsOpen(false); }}
                            disabled={isLoading || isFormDisabled}
                        >
                            <FaSave className="mr-3 h-4 w-4 text-green-500" />
                            Salvar
                        </button>
                        <button type="button"
                            className={itemClass}
                            onClick={() => { onCancel(); setIsOpen(false); }}
                            disabled={isLoading || !hasContractId || isFormDisabled}
                        >
                            <FaBan className="mr-3 h-4 w-4 text-orange-500" />
                            Cancelar Contrato
                        </button>
                        <button type="button"
                            className={itemClass}
                            onClick={() => { onFinish(); setIsOpen(false); }}
                            disabled={isLoading || !hasContractId || isFormDisabled}
                        >
                            <FaCheck className="mr-3 h-4 w-4 text-green-500" />
                            Finalizar Contrato
                        </button>
                        {contractStatus === STATUS_FINISHED && canAmend && onAmendment && (
                            <button type="button"
                                className={itemClass}
                                onClick={() => { onAmendment(); setIsOpen(false); }}
                                disabled={isLoading || !hasContractId}
                            >
                                <FaFileContract className="mr-3 h-4 w-4 text-purple-500" />
                                Aditar Contrato
                            </button>
                        )}
                        {BILLING_ACTION_ENABLED && onBilling && (
                            <button type="button"
                                className={itemClass}
                                onClick={() => { onBilling(); setIsOpen(false); }}
                                disabled={isLoading || !hasContractId || contractStatus !== STATUS_FINISHED || isBilled}
                            >
                                <FaFileInvoiceDollar className="mr-3 h-4 w-4 text-blue-500" />
                                Faturar
                            </button>
                        )}
                        {creditOperationId !== undefined && (
                            <button type="button"
                                className={itemClass}
                                onClick={() => { setIsRegisterModalOpen(true); setIsOpen(false); }}
                                disabled={isLoading || !canRequestRegister}
                            >
                                <FaClipboardCheck className="mr-3 h-4 w-4 text-blue-500" />
                                Solicitar Registro
                            </button>
                        )}
                    </div>
                </div>
            )}

            <ConfirmationModal
                isOpen={isRegisterModalOpen}
                onCancel={() => setIsRegisterModalOpen(false)}
                onConfirm={() => { setIsRegisterModalOpen(false); handleRequestRegister(); }}
                id="request-register-modal"
                title="Solicitar Registro"
                text="Tem certeza que deseja registrar o contrato na B3, Pismo e Dimensa?"
            />
        </div>
    );
};

export default ContractActionDropdown;
